import React, { useState, useEffect, useCallback } from 'react';
import { Search, Mail, User, MessageSquare, Clock, X, Loader2, ExternalLink, Archive, Trash2, CheckCircle, ArrowRight } from 'lucide-react';
import api, { ContactSubmission, ContactSubmissionStats } from '../services/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'read', label: 'Read', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  { value: 'replied', label: 'Replied', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'archived', label: 'Archived', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'spam', label: 'Spam', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
];

const ContactSubmissionsPage: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [stats, setStats] = useState<ContactSubmissionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Detail panel
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getContactSubmissions({
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        search: searchQuery || undefined,
      });
      setSubmissions(data);
      setError(null);
    } catch (err) {
      setError('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.getContactSubmissionStats();
      setStats(data);
    } catch (err) {
      // Stats are non-critical
    }
  }, []);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSelectSubmission = async (submission: ContactSubmission) => {
    setDetailLoading(true);
    try {
      const detail = await api.getContactSubmission(submission.id);
      setSelectedSubmission(detail);
      setInternalNotes(detail.internal_notes || '');
      // Refresh stats if it was new (now marked as read)
      if (submission.status === 'new') {
        fetchStats();
        fetchSubmissions();
      }
    } catch (err) {
      setError('Failed to load submission details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedSubmission) return;
    try {
      const updated = await api.updateContactSubmission(selectedSubmission.id, { status: status as any });
      setSelectedSubmission(updated);
      fetchSubmissions();
      fetchStats();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedSubmission) return;
    try {
      const updated = await api.updateContactSubmission(selectedSubmission.id, { internal_notes: internalNotes });
      setSelectedSubmission(updated);
    } catch (err) {
      alert('Failed to save notes');
    }
  };

  const handleConvertToDeal = async () => {
    if (!selectedSubmission) return;
    if (!window.confirm('Convert this contact to a deal in the pipeline?')) return;
    try {
      const result = await api.convertContactSubmissionToDeal(selectedSubmission.id);
      if (result.success) {
        alert('Contact converted to deal successfully!');
        setSelectedSubmission(null);
        fetchSubmissions();
        fetchStats();
        // Optionally redirect to the deal
        window.open(`/pipeline?deal=${result.deal_id}`, '_blank');
      }
    } catch (err) {
      alert('Failed to convert to deal');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else if (diffHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getStatusBadge = (status: string) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return option ? (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option.color}`}>
        {option.label}
      </span>
    ) : null;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Contact Submissions
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage contact form submissions from digimint.co
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-blue-500">
              <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">New</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.read}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Read</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border-l-4 border-green-500">
              <div className="text-2xl font-bold text-green-600">{stats.replied}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Replied</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.archived}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Archived</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.spam}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Spam</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search by name, email, or subject..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Submissions List */}
          <div className={`flex-1 ${selectedSubmission ? 'hidden lg:block' : ''}`}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No submissions found
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Contact form submissions will appear here.
                </p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow divide-y divide-gray-200 dark:divide-gray-700">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    onClick={() => handleSelectSubmission(submission)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedSubmission?.id === submission.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    } ${submission.status === 'new' ? 'border-l-4 border-blue-500' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {submission.name}
                          </span>
                          {getStatusBadge(submission.status)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300 truncate mb-1">
                          {submission.subject}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {submission.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(submission.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {submission.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selectedSubmission && (
            <div className="w-full lg:w-[450px] flex-shrink-0">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow sticky top-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Submission Details</h3>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {detailLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="p-4 space-y-4">
                    {/* Contact Info */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-5 w-5 text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white text-lg">
                          {selectedSubmission.name}
                        </span>
                      </div>
                      <a
                        href={`mailto:${selectedSubmission.email}`}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 text-sm"
                      >
                        <Mail className="h-4 w-4" />
                        {selectedSubmission.email}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Subject
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedSubmission.subject}</p>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Message
                      </label>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/50 rounded p-3 text-sm">
                        {selectedSubmission.message}
                      </p>
                    </div>

                    {/* Meta Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Submitted
                        </label>
                        <p className="text-gray-900 dark:text-white">
                          {new Date(selectedSubmission.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Source Page
                        </label>
                        <p className="text-gray-900 dark:text-white">{selectedSubmission.source_page}</p>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-2">
                        Status
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {STATUS_OPTIONS.map(option => (
                          <button
                            key={option.value}
                            onClick={() => handleUpdateStatus(option.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              selectedSubmission.status === option.value
                                ? option.color + ' ring-2 ring-offset-1 ring-blue-500'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Internal Notes */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-2">
                        Internal Notes
                      </label>
                      <textarea
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        onBlur={handleSaveNotes}
                        rows={3}
                        placeholder="Add internal notes..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Actions */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                      <a
                        href={`mailto:${selectedSubmission.email}?subject=Re: ${encodeURIComponent(selectedSubmission.subject)}`}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Mail className="h-4 w-4" />
                        Reply via Email
                      </a>
                      <button
                        onClick={handleConvertToDeal}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <ArrowRight className="h-4 w-4" />
                        Convert to Pipeline Deal
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateStatus('archived')}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Archive className="h-4 w-4" />
                          Archive
                        </button>
                        <button
                          onClick={() => handleUpdateStatus('spam')}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                          Mark Spam
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactSubmissionsPage;
